import { resolveEntitySelectorConfig, type FieldValue } from '../components/workspace/dynamicFormFieldUtils';
import type { FormFieldDefinition } from '../types/formBuilder';

export const HOSPITAL_PARENT_ALIASES = [
  'hospitalId',
  'hospital',
  'selectedHospitalId',
  'assignedHospitalId',
] as const;

export const HCP_PARENT_ALIASES = [
  'hcpId',
  'hcp',
  'selectedHcpId',
  'assignedHcpId',
] as const;

function readFirstAlias(
  source: Record<string, string> | Record<string, FieldValue>,
  aliases: readonly string[],
): string | undefined {
  for (const alias of aliases) {
    const raw = source[alias as keyof typeof source];
    if (raw === null || raw === undefined) continue;
    const value = String(raw).trim();
    if (value) return value;
  }
  return undefined;
}

export function withLegacyEntityParentKeys(parentIds: Record<string, string>): Record<string, string> {
  const next = { ...parentIds };
  const hospitalId = readFirstAlias(next, HOSPITAL_PARENT_ALIASES);
  const hcpId = readFirstAlias(next, HCP_PARENT_ALIASES);

  if (hospitalId) {
    next.hospitalId = hospitalId;
    next.Hospital = hospitalId;
  }
  if (hcpId) {
    next.hcpId = hcpId;
    next.HCP = hcpId;
  }

  return next;
}

export function normalizePatientParentIds(
  parentIds: Record<string, string>,
  formData: Record<string, FieldValue>,
  fields?: FormFieldDefinition[],
): Record<string, string> {
  const normalized = withLegacyEntityParentKeys(parentIds);

  if (fields?.length) {
    for (const field of fields) {
      const config = resolveEntitySelectorConfig(field);
      if (!config) continue;
      const entityType = config.entityType?.toUpperCase();
      const selectedValue = formData[field.internalName];
      const selectedId = selectedValue ? String(selectedValue).trim() : '';
      if (!selectedId) continue;

      if (entityType === 'HOSPITAL' && !normalized.hospitalId) {
        normalized.hospitalId = selectedId;
      }
      if (entityType === 'HCP' && !normalized.hcpId) {
        normalized.hcpId = selectedId;
      }
    }
  }

  if (!normalized.hospitalId) {
    const aliasHospitalId = readFirstAlias(formData, HOSPITAL_PARENT_ALIASES);
    if (aliasHospitalId) normalized.hospitalId = aliasHospitalId;
  }
  if (!normalized.hcpId) {
    const aliasHcpId = readFirstAlias(formData, HCP_PARENT_ALIASES);
    if (aliasHcpId) normalized.hcpId = aliasHcpId;
  }

  return withLegacyEntityParentKeys(normalized);
}

export function normalizeHospitalParentIds(
  parentIds: Record<string, string>,
  formData: Record<string, FieldValue>,
  fields?: FormFieldDefinition[],
): Record<string, string> {
  const normalized = withLegacyEntityParentKeys(parentIds);

  if (fields?.length) {
    for (const field of fields) {
      const config = resolveEntitySelectorConfig(field);
      if (!config) continue;
      const entityType = config.entityType?.toUpperCase();
      if (entityType !== 'HOSPITAL') continue;

      const selectedValue = formData[field.internalName];
      const selectedId = selectedValue ? String(selectedValue).trim() : '';
      if (selectedId && !normalized.hospitalId) {
        normalized.hospitalId = selectedId;
      }
    }
  }

  if (!normalized.hospitalId) {
    const aliasHospitalId = readFirstAlias(formData, HOSPITAL_PARENT_ALIASES);
    if (aliasHospitalId) normalized.hospitalId = aliasHospitalId;
  }

  return withLegacyEntityParentKeys(normalized);
}
